// What a ballot paper has to show, in types.
//
// Everything the ARITHMETIC needs already has a name in @cca/decides-count, and
// these types build on those rather than restating them — so a rule that moves
// in the engine moves here too. What is added is only what a voter must see and
// the count has no opinion about: the words of the motion, who moved it, and who
// is holding the paper.
//
// ── BUT THE WIRE IS NOT THE ENGINE, AND THAT DISTINCTION COST A ROUND ──────
// Reconciled against `@cca/decides-worker` on 2026-08-27, after this half of the
// cast path and the worker half were built in parallel against two different
// pictures of the same three routes. The shapes that cross the wire —
// {@link PaperCandidate}, {@link PaperMark}, {@link MarkableItem} — are now the
// WORKER's, spelt its way, and are **deliberately not derived from the engine's
// types**. The engine's `PositionChoice` is a tagged union it counts with; the
// wire's `choice` is one of four WORDS from `mark_choice_is_known`
// (`migrations/0001_initial.sql:602`). Deriving one from the other reads as
// rigour and produces a body the route rejects — which is exactly what happened.
// Where a wire value is a plain string that the DATABASE fences to a known set
// ({@link asBallotMode}, {@link isElectorate}, {@link paperHouseLabel}), it is
// narrowed here, once, rather than asserted in the client and believed.
//
// Type-only imports: this package does not call `count()` and does not bundle
// the engine. That holds for the results sheet too — it renders a `CountResult`
// it is handed and never computes one (see ./results/view.ts).

import type {
  BallotMode,
  Electorate,
  House,
  MajorityBase,
  MotionItem,
  MotionVote,
} from '@cca/decides-count';

/**
 * The choices a voter can MARK on a paper.
 *
 * `blank` is excluded deliberately, and by subtraction from the engine's own
 * union rather than by writing three strings out again. A blank paper is one
 * returned with nothing marked — it is a state you reach by submitting an
 * unmarked paper, never a fourth radio button. And not voting at all is a THIRD
 * thing again, reached by not submitting: the engine keeps `abstain`, `blank`
 * and absent distinct all the way through to the result, so the paper must not
 * collapse them here.
 *
 * IT DRIVES THE FIXTURE MOTION PAPER ONLY, and nothing on this path crosses the
 * wire. The three words happen to be the worker's `MOTION_CHOICES` minus
 * `blank` — but when the motion CAST path is built, its body must be taken from
 * `@cca/decides-worker/src/cast.ts` and not from this line: the coincidence is
 * not a contract, and reading it as one is the mistake the header records.
 */
export type MarkableChoice = Exclude<MotionVote['choice'], 'blank'>;

/**
 * The word "Abstain", written once.
 *
 * It appears on BOTH papers — the motion's third row and the position paper's
 * last — and the results sheet reuses these same labels for its row headings
 * (the README records that "Abstain" rather than "Abstained" is what made the
 * tally table fit at 320px). Three surfaces, one spelling.
 */
export const ABSTAIN_LABEL = 'Abstain';

/**
 * SO 15.7's own words, and deliberately NOT "None of the above" or "Re-open
 * nominations". It is a lawful choice on every election and a winning one — the
 * places it takes are unfilled and go to Standing Committee (SO 15.12) — so it
 * is named the way the Standing Orders name it.
 */
export const NO_CONFIDENCE_LABEL = 'No Confidence';

/** The vote rows, in the order they appear on the paper. Abstain is a listed
 *  option at equal weight with Aye and No — it is a recorded decision. */
export const VOTE_OPTIONS: ReadonlyArray<{ value: MarkableChoice; label: string }> = [
  { value: 'aye', label: 'Aye' },
  { value: 'no', label: 'No' },
  { value: 'abstain', label: ABSTAIN_LABEL },
];

/** How each House is named to the person in it. */
export const HOUSE_LABEL: Record<House, string> = {
  clergy: 'House of Clergy',
  laity: 'House of Laity',
};

/**
 * The House STAMPED on a paper, in words — and there are THREE of those words,
 * not two.
 *
 * `paper.voter_house` carries `elector.house`'s vocabulary
 * (`CHECK (house IN ('clergy', 'laity', 'bishop'))`) narrowed by
 * `voterHouseForPaper` (`@cca/decides-worker/src/cast.ts`): on a SECRET ballot
 * the Bishop votes on a `clergy`-stamped paper, because on a secret paper
 * `'bishop'` is not a category but a NAME — the roll admits at most one. On an
 * ATTRIBUTABLE ballot, where every vote is published by design, the stamp really
 * is `'bishop'`. So a two-value table is right for the ELECTION this screen
 * marks and wrong for the paper type it reads, and it would render one
 * identified man's own House as a blank beside his address.
 *
 * AN UNKNOWN WORD RETURNS `null` AND THE LINE IS THEN NOT DRAWN — the direction
 * is chosen, not incidental. The stamp decides nothing this screen does: what
 * may be marked is the server's `entitled`, per item, and what may be recorded
 * is re-decided on the request that records it. Refusing a member their paper
 * over a word this build cannot spell would be disenfranchisement to protect a
 * caption, which is the one direction Composition of Synod cl 9(1)-(2) forbids
 * this platform.
 */
export function paperHouseLabel(voterHouse: string): string | null {
  switch (voterHouse) {
    case 'clergy':
    case 'laity':
      return HOUSE_LABEL[voterHouse];
    case 'bishop':
      return 'The Bishop';
    default:
      return null;
  }
}

/**
 * The ballot's mode, narrowed from the wire's plain string.
 *
 * `ballot.mode` is one third of nearly every primary key in the schema and is
 * fenced by `CHECK (mode IN ('live', 'rehearsal', 'sandbox'))` on every table
 * that carries it, so an unknown word is not reachable without a migration. It
 * is narrowed here anyway, once, rather than declared as {@link BallotMode} on
 * the wire type and believed — because the consequence of being wrong is
 * asymmetric. The band exists "so a rehearsal banner is not optional decoration"
 * (the engine's own note), and a band keyed on an unchecked string fails by
 * DISAPPEARING: an unrecognised mode renders as a live vote. `null` is refused
 * by the cast screen, which is the other direction.
 */
export function asBallotMode(value: string): BallotMode | null {
  return value === 'live' || value === 'rehearsal' || value === 'sandbox' ? value : null;
}

/**
 * Whether the papers on this ballot may be attributed to the members who cast
 * them — `ballot.secrecy`, which is `secret` on every election (SO 15.9, and a
 * CHECK) and either word on a motion.
 *
 * A SECOND DECLARATION OF THE SAME UNION, deliberately: `BallotSecrecy` in
 * results/view.ts is the results sheet's name for it, and that module already
 * imports THIS one — so naming it there and reading it here would be an import
 * cycle. The column is the source of truth for both.
 */
export type PaperSecrecy = 'secret' | 'attributable';

/**
 * The ballot's secrecy, narrowed from the wire's plain string, for the ONE
 * reader that has to say something true about publication after the paper has
 * gone: the confirmation panel.
 *
 * **`null` — a word this build cannot name — means SAY NOTHING**, and the
 * direction is chosen. The alternative is a screen that states, on the
 * confirmation of a binding vote, either that the ballot was secret or that it
 * was published, on the strength of a word it could not read; and of the two,
 * only silence is honest. The mark is already recorded by then, so nothing here
 * gates a vote — this narrowing decides a sentence, never an act.
 */
export function asSecrecy(value: string): PaperSecrecy | null {
  return value === 'secret' || value === 'attributable' ? value : null;
}

/** The member holding this paper. Their House is not decoration: it decides
 *  which positions they may mark (SO 15.8) and which tally their vote lands in
 *  (SO 8.6), so it stays visible on the paper rather than in a profile menu. */
export interface Elector {
  /** Display name. Identity is `Contacts.id` elsewhere in the platform — never
   *  an email address, and never this string. */
  name: string;
  house: House;
}

/**
 * A motion as it appears on paper: the engine's own item, plus the text a voter
 * reads. `title` is required here even though {@link MotionItem} leaves it
 * optional — an untitled motion is not something anyone can be asked to vote on.
 */
export interface MotionPaper {
  item: MotionItem & { title: string };
  /**
   * The instrument — the words Synod is being asked to agree, verbatim. Rendered
   * in full, always: never truncated, never behind a "show more", always
   * selectable. A voter who cannot read the whole motion cannot consent to it.
   */
  instrument: string;
  mover: string;
  seconder: string;
}

// ── The position paper ─────────────────────────────────────────────────────
// An ELECTION, not a motion: candidates, places, and No Confidence.
//
// **AND THIS IS THE HALF THAT IS NOT BUILT ON THE ENGINE'S TYPES.** The motion
// paper above renders a fixture and computes against @cca/decides-count, so the
// rule there — a shape that moves in the engine moves here — is right. Nothing
// below it crosses a wire either. Everything from here down DOES: it is what a
// real elector's paper carries and what their mark is submitted as, so it is
// spelt the way `@cca/decides-worker` spells it and derived from nothing. See
// the header for what deriving it from the engine cost.
//
// What a POSITION paper does not have, and the absence is the design:
//  • no free-text field, anywhere, ever. A write-in box on a secret ballot is a
//    de-anonymisation channel with a keyboard attached — so when a member says
//    "my candidate isn't listed", the answer is a word with the Synod Admin and
//    never an input on this screen.
//  • no tally, no turnout, no countdown. Same rule as the motion paper.

/**
 * WHAT THE SERVER IS TOLD when a row is the one marked — **the WIRE's
 * vocabulary, and deliberately not the counting engine's.**
 *
 * `POST /api/ballot/:ballotId/marks` reads `{ itemId, choice, candidateId? }`
 * and tests `choice` against four words (`POSITION_CHOICES`,
 * `@cca/decides-worker/src/cast.ts`), which are a subset of the schema's own
 * `mark_choice_is_known`. **`blank` is the fourth, and it is still excluded here
 * even though this surface can now cast one** (registrar ruling 2026-08-27).
 * That is not an oversight and it is not the old refusal left standing: a blank
 * is a fact about the PAPER, not the mark on a row, and the wire says so — it is
 * sent as `{ marks: [] }`, which the route reads as "one `blank` row for every
 * entitled item and no row for the rest". So there is no row whose `mark` is
 * `blank`, `positionRows` cannot build one, and the act lives in
 * cast/BlankConfirm.tsx where its warning does.
 *
 * THIS TYPE USED TO BE `Exclude<PositionChoice, { kind: 'blank' }>` — the
 * engine's tagged union — and that was the defect this file's header records.
 * The two are not the same thing and never were: the engine's union is what a
 * COUNT is computed over, while these words are what a ROUTE parses. `readMarks`
 * requires `typeof mark.choice === 'string'` and answers **`400 bad_request`** to
 * anything else, so every paper this screen submitted would have been refused —
 * on a live synod election, at the one moment that cannot be retried.
 *
 * `candidateId` is present IFF `choice` is `'candidate'`, in both directions,
 * because the route checks it in both directions
 * (`mark_candidate_id_iff_a_candidate_choice`) and answers `item_not_markable`
 * to a candidate id smuggled onto an abstention. Expressed as a union rather
 * than an optional field so the browser cannot build the refused shape at all.
 */
export type PaperMark =
  | { choice: 'candidate'; candidateId: string }
  | { choice: 'no-confidence' }
  | { choice: 'abstain' };

/**
 * A candidate as the worker serves them — `PaperCandidate` in
 * `@cca/decides-worker/src/cast.ts`, field for field and spelt its way.
 *
 * It is NOT the engine's `Candidate & { name: string }`, which is what this type
 * used to be: `id`/`name` against the wire's `candidateId`/`displayName` gave
 * every radio row the key `candidate:undefined` and an empty label, and sent a
 * candidate id of `undefined` to a binding election. Every rule in the count
 * keys on the id; the display name is for the reader's eyes only.
 */
export interface PaperCandidate {
  candidateId: string;
  displayName: string;
}

/** One row of a position paper: what it says, and what marking it means. */
export interface PositionRow {
  /**
   * Unique within one paper, and NAMESPACED rather than being the bare
   * candidate id. The engine models No Confidence as a union arm instead of a
   * reserved candidate id because "a reserved id is a collision waiting for the
   * first candidate whose id happens to match it" (its `Contender` type) — and a
   * bare key here would reintroduce exactly that collision in the browser, where
   * the difference between "A was elected" and "the seat is unfilled" would turn
   * on a string.
   */
  key: string;
  label: string;
  /** What the server is told when this row is the one marked. */
  mark: PaperMark;
}

/**
 * The rows of a position paper, in the order they appear.
 *
 * CANDIDATES IN THE ORDER THE SERVER GAVE THEM. This surface does not reorder
 * them and does not randomise them per paper: ballot-order effects are real, and
 * whether to randomise is a decision about the conduct of an election — the
 * registrar's, not a builder's. It is recorded in the README as not made rather
 * than made here by default.
 *
 * NO CONFIDENCE IS A ROW LIKE ANY OTHER (SO 15.7) — same element, same class,
 * same size and weight, no colour and no icon. It is a lawful choice, and a row
 * dressed as a warning would tell a member that choosing it is an error.
 *
 * Abstain is last because it is the one row that is not a CONTENDER: No
 * Confidence competes in the count, an abstention is a recorded decision not to.
 * That is an ordering, not a demotion — the two render identically.
 */
export function positionRows(candidates: readonly PaperCandidate[]): PositionRow[] {
  return [
    ...candidates.map((candidate) => ({
      key: `candidate:${candidate.candidateId}`,
      label: candidate.displayName,
      mark: { choice: 'candidate' as const, candidateId: candidate.candidateId },
    })),
    {
      key: 'no-confidence',
      label: NO_CONFIDENCE_LABEL,
      mark: { choice: 'no-confidence' as const },
    },
    { key: 'abstain', label: ABSTAIN_LABEL, mark: { choice: 'abstain' as const } },
  ];
}

/**
 * Who elects this position (SO 15.8) — a per-POSITION property, never a
 * per-ballot chamber rule. Keyed on the engine's closed union, so a fourth
 * electorate reds this file rather than rendering a paper that says nothing
 * about who may mark it.
 *
 * It is a statement, not a gate: the server decides whether this member gets a
 * paper at all, and a member holding one has already passed that test.
 */
export const ELECTORATE_SENTENCE: Record<Electorate, string> = {
  clergy: 'This position is elected by the House of Clergy.',
  laity: 'This position is elected by the House of Laity.',
  both: 'This position is elected by the Clergy and the Laity together.',
};

/**
 * Is this the wire's word for an electorate?
 *
 * `ballot_item.electorate` arrives as `string | null` — NULL is a MOTION, which
 * the whole Synod votes on and `ballot.house_rule` divides, and the three words
 * are fenced by `CHECK (electorate IS NULL OR electorate IN (…))`. The guard is
 * what lets {@link ELECTORATE_SENTENCE} be read with the wire's value without an
 * assertion: a position whose electorate this build cannot name has no sentence
 * to put above its rows, and the cast screen refuses it rather than showing a
 * paper that says nothing about who may mark it.
 */
export function isElectorate(value: string | null): value is Electorate {
  return value !== null && value in ELECTORATE_SENTENCE;
}

/**
 * What is being filled, in words.
 *
 * Called only where exactly ONE place is at stake: the cast screen refuses to
 * render a markable paper otherwise, because a multi-seat election needs a
 * different control and an over-vote guard, and a radio group would silently
 * under-vote every paper on it. So these two arms are exhaustive at every call
 * site today, and the sentence for two or more places is the one that cannot be
 * written until that guard is lifted.
 */
export function seatSentence(alternateSeats: number): string {
  return alternateSeats === 1
    ? 'One alternate place is to be filled at this election.'
    : 'One place is to be filled at this election.';
}

/**
 * The instruction above the rows, and it is the same act on BOTH papers: mark
 * one row, then submit. Short, because it is read once, standing up, under time
 * pressure.
 *
 * ONE SPELLING, TWO SURFACES — the same reason {@link ABSTAIN_LABEL} is written
 * once. Two constants holding the same sentence are two sentences the day either
 * one is edited.
 */
export const MARK_INSTRUCTION = 'Mark one row, then submit.';

/** The name cast/PositionPaper.tsx reads {@link MARK_INSTRUCTION} by. */
export const POSITION_MARK_INSTRUCTION = MARK_INSTRUCTION;

/**
 * ABSTAINING IS A DECISION — the sentence, written once.
 *
 * It is the second half of {@link POSITION_ROW_NOTE} on an election paper, and
 * it is the abstention note a `present-and-voting` MOTION carries beneath its
 * rows (`ABSTENTION_NOTE`, ./thresholds). Registrar-ratified wording; the motion
 * paper reads it from here rather than restating it, so the two papers cannot
 * come to say different things about the same choice.
 */
export const ABSTENTION_IS_A_CHOICE =
  'Abstaining is a recorded choice, and it is not the same as not voting.';

/** The note beneath the rows — the two things about this paper a member is
 *  likeliest to get wrong, and both are constitutional rather than mechanical. */
export const POSITION_ROW_NOTE = `No Confidence is a lawful choice on every election (Standing Order 15.7). ${ABSTENTION_IS_A_CHOICE}`;

/**
 * What an item this House may not decide says for itself.
 *
 * **Every item on the ballot is served, including the ones the reader may not
 * mark, and it is rendered UNMARKABLE rather than omitted** (registrar ruling
 * 2026-08-20, mirrored in `loadPaperItems`/`isEntitledToMark`). The reason is
 * given to the member in the second sentence rather than kept in a comment: a
 * paper that was simply SHORT of an item would itself be a discriminator — the
 * shape of the paper would say which House is holding it — so every paper on one
 * ballot carries the same items, and entitlement is a property of the ROWS.
 *
 * `entitled` is the server's answer and the fence is the database's
 * (`mark_subject_is_in_the_item_electorate`). This sentence explains a control
 * that is absent; it is not the control.
 */
export const UNMARKABLE_ITEM_NOTE =
  'This is not yours to mark (Standing Order 15.8). It stays on your paper so that every paper on this ballot carries the same items.';

// ── The motion paper, as a member marks it ─────────────────────────────────
// An AMENDMENT, not an election: an instrument to read, a majority it is
// measured against, and three answers to it. It crosses the same wire the
// position paper does, so — like everything from the position section down — it
// is spelt the way `@cca/decides-worker` spells it and derived from nothing.
//
// WHAT IT DOES NOT HAVE, and each absence is the design:
//  • no No Confidence. SO 15.7 is an ELECTIONS provision; there is no such
//    answer to a motion, and a fourth row would invent a lawful choice.
//  • no free-text field, no tally, no turnout, no countdown. Same rules as the
//    election paper, for the same reasons.
//  • no truncation of the instrument, no "show more", no scroll gate on the
//    submit. A member who cannot read the whole motion cannot consent to it,
//    and a gate that waited for a scroll event would be meaningless to a
//    screen-reader user and a dark pattern to everybody else.

/**
 * THE THREE ANSWERS TO A MOTION — **the WIRE's vocabulary, and it is the
 * WORKER's, not the counting engine's.**
 *
 * `MOTION_CHOICES` (`@cca/decides-worker/src/cast.ts`) is `aye`, `no`,
 * `abstain`, `blank`; the route tests a motion's `choice` against exactly that
 * set and answers `item_not_markable` to anything else. `blank` is excluded here
 * for the reason {@link PaperMark} gives on the position side — a blank is a
 * fact about the PAPER, sent as `{ marks: [] }`, and never a fourth row.
 *
 * IT IS DELIBERATELY NOT {@link MarkableChoice}, although the three strings are
 * the same three strings today. That type is `MotionVote['choice']` minus
 * `blank` — the ENGINE's union, which is what a COUNT is computed over — and its
 * own note says in terms that the coincidence is not a contract. Deriving a
 * request body from the engine is the defect this file's header records: it cost
 * a round on the position path, and it would have cost a synod sitting here.
 */
export type MotionChoice = 'aye' | 'no' | 'abstain';

/**
 * What the server is told when a row is the one marked.
 *
 * **No `candidateId`, in either direction.**
 * `mark_candidate_id_iff_a_candidate_choice` is an IFF and the route checks it
 * both ways, so a candidate id smuggled onto a motion is refused — and there is
 * no field here through which one could be sent.
 */
export interface MotionMark {
  choice: MotionChoice;
}

/** One row of a motion paper: what it says, and what marking it means. */
export interface MotionRow {
  key: MotionChoice;
  label: string;
  mark: MotionMark;
}

/**
 * THE THREE ROWS, IN THE ORDER THEY APPEAR — and they are three visual peers.
 *
 * AYE, NO, ABSTAIN, never randomised: on a motion the rows are not contenders
 * whose order could advantage one of them, they are the answer to a question
 * asked in those words.
 *
 * **ABSTAIN IS AN EQUAL PEER, NOT A LESSER ONE.** One list and no branch, so
 * there is nowhere for a colour, an icon, a smaller row or a separator to be
 * added to it — exactly as {@link positionRows} builds No Confidence alongside
 * the candidates. Abstaining is a lawful choice and a recorded one, and
 * subordinating it visually would be this platform editorialising about how a
 * member should vote.
 *
 * A MODULE CONSTANT rather than a builder function, and that is the motion
 * paper's answer to the `rows` prop `PositionPaper` takes. An election's rows
 * depend on its candidates, so they are built once by the parent and passed
 * down, or the row the member marked and the mark sent to the server would be
 * read from two lists. A motion's rows depend on nothing: there is exactly one
 * list, and both halves read it by construction.
 */
export const MOTION_ROWS: readonly MotionRow[] = [
  { key: 'aye', label: 'Aye', mark: { choice: 'aye' } },
  { key: 'no', label: 'No', mark: { choice: 'no' } },
  { key: 'abstain', label: ABSTAIN_LABEL, mark: { choice: 'abstain' } },
];

/**
 * THE INSTRUMENT, SPLIT INTO PARAGRAPHS — and nothing else done to it.
 *
 * A blank line starts a new paragraph. That is the whole rule: no markdown, no
 * lists, no headings, no links, no auto-linking — and above all **no
 * `dangerouslySetInnerHTML`**. The instrument is operator-entered prose that
 * every member of Synod reads on a binding ballot; rendering markup out of it
 * would make the text of the motion an injection surface. Each paragraph below
 * is handed to React as the text child of a real `<p>`, which escapes it.
 *
 * NOTHING IS DROPPED. Each paragraph is trimmed only of the whitespace that
 * separated it from its neighbour — HTML collapses that whitespace anyway, so
 * the rendered words are the stored words, entire and in order. An instrument
 * with no words at all returns an EMPTY LIST, which is what the cast screen
 * refuses on: a title is not a motion.
 */
export function instrumentParagraphs(instrument: string): string[] {
  return instrument
    .split(/\n\s*\n/)
    .map((paragraph) => paragraph.trim())
    .filter((paragraph) => paragraph.length !== 0);
}

/**
 * THE ONE MOTION THIS SCREEN WILL PUT TO A MEMBER — the motion's
 * {@link MarkableItem}, narrowed out of the wire's item and refused if it cannot
 * be.
 *
 * It exists so the refusals happen ONCE, at the boundary, and everything
 * downstream is total: `base` has a ratified sentence, `paragraphs` is non-empty
 * and already split, and there is no arm in which a motion is half-renderable.
 * The gates that produce it are in cast/CastPage.tsx, with the refusal copy.
 */
export interface MarkableMotion {
  itemId: string;
  title: string;
  /**
   * The majority this motion is measured against, and the key into
   * `THRESHOLD_COPY`. **Narrowed, never defaulted** — see `isMajorityBase`
   * (./thresholds) for what a substituted value would print on a two-thirds
   * amendment.
   */
  base: MajorityBase;
  /** The instrument, verbatim and entire, already split by
   *  {@link instrumentParagraphs}. At least one, guaranteed by the gate. */
  paragraphs: string[];
  /**
   * Who moved it and who seconded it, as the record holds them.
   *
   * `null` where it does not, and the line is then simply not drawn — the same
   * direction as {@link paperHouseLabel}. A motion is moved and seconded before
   * it reaches a ballot, so a missing name is a gap in the RECORD; refusing a
   * member their paper over it would be disenfranchisement to protect a caption.
   */
  mover: string | null;
  seconder: string | null;
}

/**
 * The ONE item this screen will put to a member, narrowed out of the wire's
 * item and refused if it cannot be.
 *
 * It exists so that the refusals happen ONCE, at the boundary, and everything
 * downstream is total: `electorate` has a sentence, `alternateSeats` is a
 * number, and there is no arm left in which a paper is half-renderable. The
 * gates that produce it are in cast/CastPage.tsx, which is also where the
 * refusal copy lives.
 */
export interface MarkableItem {
  itemId: string;
  title: string;
  electorate: Electorate;
  alternateSeats: number;
  /** In the order the server gave them. This client does not reorder. */
  candidates: PaperCandidate[];
}

/** Everything on the screen: one item of a ballot, as seen by one member. */
export interface BallotPaperView {
  ballotId: string;
  /** `live` | `rehearsal` | `sandbox`, carried from the engine's own union so a
   *  rehearsal can never be dressed as a live vote. */
  mode: BallotMode;
  /** Whether this item is still accepting votes. Shown as a filled/hollow dot
   *  AND the word — nothing in this product is signalled by colour alone. */
  state: 'open' | 'closed';
  /** Position in the agenda, 1-based, for the "ITEM 2 OF 5" eyebrow. */
  itemNumber: number;
  itemCount: number;
  elector: Elector;
  paper: MotionPaper;
}
